import { useFlag } from 'shared/settings/FeatureToggles';
import { useEstimateSeatsUpgrade } from '../SubscriptionAPI';
import {
  Column,
  Columns,
  DrawerActions,
  Stack,
  Inline,
} from '@superdispatch/ui';
import { PastDueInvoice } from './PastDueInvoice';
import {
  Alert,
  Box,
  Button,
  DescriptionLineItem,
  TextBox,
} from '@superdispatch/ui-lab';
import { Skeleton } from '@material-ui/lab';
import { Collapse, Link, Typography } from '@material-ui/core';
import { ExpandLess, ExpandMore } from '@material-ui/icons';
import { useState } from 'react';
import { formatCurrency } from 'shared/helpers/IntlHelpers';
import styled from 'styled-components';

interface PlanUpdateDrawerActionsProps {
  isSubmitting: boolean;
  seatsCount: number;
  purchasedSeatsCount: number;
  errorMsg?: string;
}

// Keeps MUI Link `component` prop typing, lost by styled().
const BreakdownToggle = styled(Link)`
  &:focus {
    outline: none;
  }
` as typeof Link;

export function PlanUpdateDrawerActions({
  isSubmitting,
  seatsCount,
  purchasedSeatsCount,
  errorMsg,
}: PlanUpdateDrawerActionsProps) {
  const isPaymentPastDueEnabled = useFlag('payment_past_due');
  const { data: taxPrice, isLoading } = useEstimateSeatsUpgrade(seatsCount);
  const pastDueInvoices = taxPrice?.past_due_invoices;
  const additionalSeatsCharge =
    Math.max(seatsCount - purchasedSeatsCount, 0) *
    (taxPrice?.plan?.unit_price ?? 0);
  const [isBreakdownExpanded, setIsBreakdownExpanded] = useState(false);

  return (
    <DrawerActions>
      <Box width="100%" paddingTop="small" paddingBottom="small">
        <Stack space="medium">
          <Stack space="large">
            <Stack space="small">
              <BreakdownToggle
                component="button"
                type="button"
                color="primary"
                underline="none"
                aria-expanded={isBreakdownExpanded}
                onClick={() => {
                  setIsBreakdownExpanded(!isBreakdownExpanded);
                }}
              >
                <Typography variant="body1" noWrap={false}>
                  <Inline verticalAlign="center">
                    {isBreakdownExpanded
                      ? 'Hide price breakdown'
                      : 'Show price breakdown'}
                    {isBreakdownExpanded ? (
                      <ExpandLess fontSize="small" />
                    ) : (
                      <ExpandMore fontSize="small" />
                    )}
                  </Inline>
                </Typography>
              </BreakdownToggle>

              <Collapse in={isBreakdownExpanded}>
                <Stack space="small">
                  {isPaymentPastDueEnabled && !!pastDueInvoices?.length && (
                    <PastDueInvoice pastDueInvoices={pastDueInvoices} />
                  )}
                  <Stack space="xxsmall">
                    <DescriptionLineItem
                      title={
                        <TextBox variant="body-semibold" color="primary">
                          Additional Seat Charge Today
                        </TextBox>
                      }
                    >
                      {isLoading ? (
                        <Skeleton width="60px" height="20px" />
                      ) : (
                        <TextBox align="right" color="primary">
                          {formatCurrency(additionalSeatsCharge)}
                        </TextBox>
                      )}
                    </DescriptionLineItem>

                    <TextBox color="secondary">Prorated</TextBox>
                  </Stack>

                  <Stack space="xsmall">
                    <DescriptionLineItem
                      title={
                        <TextBox variant="body-semibold" color="primary">
                          Subtotal
                        </TextBox>
                      }
                    >
                      {isLoading ? (
                        <Skeleton width="60px" height="20px" />
                      ) : (
                        <TextBox align="right" color="primary">
                          {formatCurrency(taxPrice?.sub_total as number)}
                        </TextBox>
                      )}
                    </DescriptionLineItem>
                    {taxPrice?.taxes && taxPrice.taxes.length > 0 && (
                      <Columns>
                        <Column>
                          <Box />
                        </Column>
                        <Column width="2/3">
                          <Stack align="right" space="xxsmall">
                            {taxPrice.taxes.map((tax) => {
                              return (
                                <DescriptionLineItem
                                  key={tax.name}
                                  title={
                                    <TextBox color="secondary">
                                      {tax.description}
                                    </TextBox>
                                  }
                                >
                                  <TextBox align="right" color="secondary">
                                    {formatCurrency(tax.amount as number)}
                                  </TextBox>
                                </DescriptionLineItem>
                              );
                            })}
                          </Stack>
                        </Column>
                      </Columns>
                    )}
                  </Stack>
                </Stack>
              </Collapse>

              <DescriptionLineItem
                title={<TextBox variant="heading-4">Total Due Today</TextBox>}
              >
                {isLoading ? (
                  <Skeleton width="64px" height="24px" />
                ) : (
                  <TextBox variant="heading-4" align="right">
                    {formatCurrency(taxPrice?.total)}
                  </TextBox>
                )}
              </DescriptionLineItem>
            </Stack>

            <Button
              size="large"
              fullWidth={true}
              type="submit"
              pending={isSubmitting}
            >
              Confirm & Pay {formatCurrency(taxPrice?.total)}
            </Button>
          </Stack>

          {errorMsg && (
            <Box maxWidth="430px">
              <Alert severity="critical">{errorMsg}</Alert>
            </Box>
          )}
        </Stack>
      </Box>
    </DrawerActions>
  );
}
